/**
 * Venue pricing visibility — search cards, venue booking sidebar, booking form/emails, etc.
 *
 * ## How to switch behaviour
 *
 * Set these in `.env` (or your host’s env) and restart the dev server / redeploy:
 *
 * Per-venue **Show / Hide prices** is managed in admin at `/admin/venues/pricing` (`venues.hide_price` in the API).
 * When the API sends `hide_price`, that value wins over env lists below.
 *
 * ### `REACT_APP_PRICING_VISIBILITY_MODE`
 * - **`whitelist`** (default) — Only venues listed in `REACT_APP_VENUES_WITH_PUBLIC_PRICING`
 *   show prices when the API has no `hide_price` yet (legacy fallback).
 * - **`blacklist`** — Everyone shows prices unless listed in `REACT_APP_VENUES_HIDE_PRICE_IDS` or API hides.
 * - **`api_only`** — Only `venue.hide_price` from the API; lists are ignored.
 *
 * ### `REACT_APP_VENUES_WITH_PUBLIC_PRICING` (optional)
 * Comma-separated venue UUIDs that may show prices in **whitelist** mode.
 * Example: `660e8400-e29b-41d4-a716-446655440007,660e8400-e29b-41d4-a716-446655440099`
 *
 * ### `REACT_APP_VENUES_HIDE_PRICE_IDS` (optional)
 * Comma-separated UUIDs that hide prices in **blacklist** mode.
 */

const parseIdList = (raw, fallback = []) => {
  if (raw == null || String(raw).trim() === '') {
    return [...fallback];
  }
  return String(raw)
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);
};

export const PRICING_VISIBILITY_MODE = (
  process.env.REACT_APP_PRICING_VISIBILITY_MODE || 'whitelist'
).toLowerCase();

/** Venues that show prices in UI + booking when mode is `whitelist`. Default: Kasa Café (seed data). */
export const VENUES_WITH_PUBLIC_PRICING_IDS = parseIdList(
  process.env.REACT_APP_VENUES_WITH_PUBLIC_PRICING,
  ['660e8400-e29b-41d4-a716-446655440007']
);

/** Venues that hide prices when mode is `blacklist`. */
export const VENUES_HIDE_PRICE_IDS = parseIdList(process.env.REACT_APP_VENUES_HIDE_PRICE_IDS, []);

const venueIdInList = (venue, ids) => {
  if (venue?.id == null || !ids?.length) return false;
  const id = String(venue.id);
  return ids.includes(id) || ids.includes(Number(venue.id));
};

/**
 * In whitelist mode: hide search price filter and price-based sort (not meaningful for mostly contact-for-pricing venues).
 */
export const isSearchPriceFilterEnabled = () => PRICING_VISIBILITY_MODE !== 'whitelist';

/**
 * Check if pricing should be shown for a venue (cards, sidebar, booking cost summary, etc.).
 * @param {Object} venue
 * @returns {boolean}
 */
const venueHasApiPricingVisibility = (venue) =>
  venue.hide_price != null || venue.hide_pricing != null;

export const shouldShowVenuePricing = (venue) => {
  if (!venue) return true;

  if (venueHasApiPricingVisibility(venue)) {
    return !(venue.hide_price === true || venue.hide_pricing === true);
  }

  const apiHides = venue.hide_price === true || venue.hide_pricing === true;

  if (PRICING_VISIBILITY_MODE === 'api_only') {
    return !apiHides;
  }

  if (PRICING_VISIBILITY_MODE === 'whitelist') {
    if (apiHides) return false;
    return venueIdInList(venue, VENUES_WITH_PUBLIC_PRICING_IDS);
  }

  if (PRICING_VISIBILITY_MODE === 'blacklist') {
    if (apiHides) return false;
    if (venueIdInList(venue, VENUES_HIDE_PRICE_IDS)) return false;
    return true;
  }

  return !apiHides;
};

/** Admin UI: saved hide_price from API when present, else inferred from env rules. */
export const getVenueHidePriceForAdmin = (venue) => !shouldShowVenuePricing(venue);

/**
 * Default full-venue hourly rate used as the baseline for “discounted” dynamic pricing.
 * Matches common venue field names across the app.
 */
export const getDefaultFullVenueHourlyRate = (venue) => {
  if (!venue) return null;
  const n = Number(venue.hourly_rate ?? venue.hourlyRate ?? venue.price);
  return Number.isFinite(n) && n > 0 ? n : null;
};

/**
 * True if any dynamic full-venue rule charges less than the venue default hourly rate.
 */
export const hasDynamicPricingDiscountVsDefault = (dynamicPricing, venue) => {
  const base = getDefaultFullVenueHourlyRate(venue);
  if (base == null || !dynamicPricing?.length) return false;
  return dynamicPricing.some((rule) => {
    const p = Number(rule.price_per_hour);
    return Number.isFinite(p) && p < base;
  });
};

/**
 * For a calendar date (YYYY-MM-DD): at least one applicable rule that day has price below default hourly.
 */
export const selectedDateHasDiscountedDynamicPricing = (dynamicPricing, selectedDateYmd, venue) => {
  const base = getDefaultFullVenueHourlyRate(venue);
  if (base == null || !selectedDateYmd || !dynamicPricing?.length) return false;
  const dateKey = String(selectedDateYmd).split('T')[0];
  const [y, m, d] = dateKey.split('-').map(Number);
  if (!Number.isFinite(y) || !Number.isFinite(m) || !Number.isFinite(d)) return false;
  const compareDate = new Date(y, m - 1, d);
  compareDate.setHours(0, 0, 0, 0);
  return dynamicPricing.some((rule) => {
    if (!rule.start_date || !rule.end_date) return false;
    const [sY, sM, sD] = rule.start_date.split('-').map(Number);
    const [eY, eM, eD] = rule.end_date.split('-').map(Number);
    const startDate = new Date(sY, sM - 1, sD);
    const endDate = new Date(eY, eM - 1, eD);
    startDate.setHours(0, 0, 0, 0);
    endDate.setHours(0, 0, 0, 0);
    if (compareDate < startDate || compareDate > endDate) return false;
    const p = Number(rule.price_per_hour);
    return Number.isFinite(p) && p < base;
  });
};
